import { useRef, useState, useMemo } from 'react';

import type { IAssistantVoiceByLanguage } from '@shared/interfaces/assistantScripts';
import {
  DEFAULT_ENGINE,
  DEFAULT_VOICE_ID,
  PollyEngine,
  PollyLanguage,
} from '@shared/interfaces/polly';
import { getVoices, getAvailableEngines } from '../constants';
import { AudioBufferPlayer } from '../audioPlayer';

interface UseAudioPreviewParams {
  activeLanguage: PollyLanguage;
  effectiveBaseLanguage: PollyLanguage;
  engine?: string;
  voiceId?: string;
  voicesByLanguage?: Partial<Record<PollyLanguage, IAssistantVoiceByLanguage>>;
  onGenerateAudio: (params: {
    text: string;
    voiceId: string;
    engine: string;
    language?: string;
  }) => Promise<ArrayBuffer>;
}

const useAudioPreview = ({
  activeLanguage,
  effectiveBaseLanguage,
  engine,
  voiceId,
  voicesByLanguage,
  onGenerateAudio,
}: UseAudioPreviewParams) => {
  const [audioPlaybackKey, setAudioPlaybackKey] = useState<
    'test' | number | null
  >(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const audioPlayerRef = useRef<AudioBufferPlayer | null>(null);

  const previewEngine = useMemo((): PollyEngine => {
    if (activeLanguage === effectiveBaseLanguage) {
      return (engine as PollyEngine | undefined) ?? DEFAULT_ENGINE;
    }

    const eventOverride = voicesByLanguage?.[activeLanguage];

    if (eventOverride?.engine) {
      return eventOverride.engine as PollyEngine;
    }

    const available = getAvailableEngines(activeLanguage, 'female');

    return (
      available[0] ?? getAvailableEngines(activeLanguage)[0] ?? DEFAULT_ENGINE
    );
  }, [activeLanguage, effectiveBaseLanguage, engine, voicesByLanguage]);

  const previewVoiceId = useMemo(() => {
    if (activeLanguage === effectiveBaseLanguage) {
      return voiceId ?? DEFAULT_VOICE_ID;
    }

    const eventOverride = voicesByLanguage?.[activeLanguage];

    if (eventOverride?.voiceId) {
      return eventOverride.voiceId;
    }

    const voices = getVoices(activeLanguage, previewEngine, 'female');

    return (
      voices[0] ??
      getVoices(activeLanguage, previewEngine)[0] ??
      DEFAULT_VOICE_ID
    );
  }, [
    activeLanguage,
    effectiveBaseLanguage,
    voiceId,
    voicesByLanguage,
    previewEngine,
  ]);

  const voiceIdRef = useRef(previewVoiceId);
  const engineRef = useRef(previewEngine);
  const activeLangRef = useRef(activeLanguage);

  voiceIdRef.current = previewVoiceId;
  engineRef.current = previewEngine;
  activeLangRef.current = activeLanguage;

  const stopAudio = () => {
    audioPlayerRef.current?.stop();
    audioPlayerRef.current = null;
    setIsAudioPlaying(false);
    setAudioPlaybackKey(null);
  };

  const playAudio = async (text: string, key: 'test' | number) => {
    setAudioPlaybackKey(key);

    try {
      const player = new AudioBufferPlayer(undefined, 0.8);
      audioPlayerRef.current = player;

      const audioData = await onGenerateAudio({
        text,
        voiceId: voiceIdRef.current,
        engine: engineRef.current,
        language: activeLangRef.current,
      });

      if (!audioData || audioData.byteLength === 0) {
        console.warn(
          `Error: no audio returned for engine: ${engineRef.current}, voiceId: ${voiceIdRef.current}`,
        );
        setAudioPlaybackKey(null);
        return;
      }

      const ctx = player.getAudioContext();
      const buffer = await ctx.decodeAudioData(audioData);

      player.load(buffer);
      setIsAudioPlaying(true);
      player.play({
        loop: false,
        onEnded: () => {
          setIsAudioPlaying(false);
          setAudioPlaybackKey(null);
        },
      });
    } catch (error) {
      console.error(error);
      setAudioPlaybackKey(null);
    }
  };

  return { audioPlaybackKey, isAudioPlaying, playAudio, stopAudio };
};

export default useAudioPreview;
